/**
 * QuestionPreview — read-mostly renderer for a single question.
 * Phase C: this component is REUSED as the workspace answer widget.
 * Keep it framework-agnostic: driven by (question, value, onChange, disabled).
 */
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";

const TERRA = "#B45309";

export default function QuestionPreview({ question: q, value, onChange, disabled }) {
  const set = (v) => onChange?.(v);

  if (q.type === "single_select_card") {
    return (
      <div className="space-y-2" data-testid={`qp-${q.id}`}>
        {(q.options || []).map((opt, i) => {
          const active = value === opt;
          return (
            <button key={`${i}-${opt}`} type="button" disabled={disabled}
              onClick={() => set(opt)}
              className={`flex min-h-14 w-full items-center justify-between rounded-xl border-2 p-4 text-left transition-all duration-150 active:scale-[0.98] ${
                active ? "border-[#B45309] bg-amber-50" : "border-neutral-200 hover:border-neutral-300"
              }`}>
              <span className="text-base font-medium text-neutral-900">{opt}</span>
              <span className={`h-4 w-4 rounded-full border-2 ${active ? "border-[#B45309] bg-[#B45309]" : "border-neutral-300"}`} />
            </button>
          );
        })}
      </div>
    );
  }

  if (q.type === "multi_select_card") {
    const arr = Array.isArray(value) ? value : [];
    const max = q.max_selections || 99;
    const toggle = (opt) => {
      if (arr.includes(opt)) set(arr.filter((x) => x !== opt));
      else if (arr.length < max) set([...arr, opt]);
    };
    return (
      <div className="space-y-2" data-testid={`qp-${q.id}`}>
        {q.max_selections && (
          <p className="text-xs text-neutral-500">Selected {arr.length} of {q.max_selections}</p>
        )}
        {(q.options || []).map((opt, i) => {
          const active = arr.includes(opt);
          return (
            <button key={`${i}-${opt}`} type="button" disabled={disabled}
              onClick={() => toggle(opt)}
              className={`flex min-h-14 w-full items-center justify-between rounded-xl border-2 p-4 text-left transition-all duration-150 active:scale-[0.98] ${
                active ? "border-[#B45309] bg-amber-50" : "border-neutral-200 hover:border-neutral-300"
              }`}>
              <span className="text-base font-medium text-neutral-900">{opt}</span>
              <span className={`flex h-5 w-5 items-center justify-center rounded border-2 ${active ? "border-[#B45309] bg-[#B45309] text-white" : "border-neutral-300"}`}>
                {active && <Check className="h-3 w-3" />}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  if (q.type === "short_text") {
    return (
      <Input data-testid={`qp-${q.id}`} value={value || ""}
        onChange={(e) => set(e.target.value)}
        disabled={disabled} maxLength={q.max_length || undefined}
        className="h-12 rounded-lg border-2 focus:border-[#B45309]" />
    );
  }

  if (q.type === "long_text") {
    return (
      <Textarea data-testid={`qp-${q.id}`} value={value || ""}
        onChange={(e) => set(e.target.value)}
        disabled={disabled} rows={q.rows || 4} maxLength={q.max_length || undefined}
        className="rounded-lg border-2 focus:border-[#B45309]" />
    );
  }

  if (q.type === "likert_5") {
    const labels = q.endpoint_labels || { low: "Strongly disagree", high: "Strongly agree" };
    return (
      <div data-testid={`qp-${q.id}`}>
        <div className="flex justify-between gap-2">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = value === n;
            return (
              <button key={n} type="button" disabled={disabled} onClick={() => set(n)}
                className={`flex h-12 flex-1 items-center justify-center rounded-full border-2 text-sm font-semibold transition ${
                  active ? "border-[#B45309] bg-[#B45309] text-white" : "border-neutral-200 text-neutral-700 hover:border-neutral-300"
                }`}>
                {n}
              </button>
            );
          })}
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] text-neutral-500">
          <span>{labels.low}</span><span>{labels.high}</span>
        </div>
      </div>
    );
  }

  if (q.type === "dropdown") {
    return (
      <Select value={value || ""} onValueChange={set} disabled={disabled}>
        <SelectTrigger data-testid={`qp-${q.id}`} className="focus:ring-[#B45309]">
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>{(q.options || []).map((o, i) => <SelectItem key={`${i}-${o}`} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  }

  if (q.type === "matrix_agree_disagree") {
    const obj = (value && typeof value === "object") ? value : {};
    const scale = q.matrix_scale || ["Strongly disagree","Disagree","Neutral","Agree","Strongly agree"];
    return (
      <div className="space-y-3" data-testid={`qp-${q.id}`}>
        {(q.matrix_statements || []).map((stmt) => (
          <div key={stmt} className="rounded-lg border border-neutral-200 p-3">
            <p className="mb-2 text-sm font-medium text-neutral-900">{stmt}</p>
            <div className="flex flex-wrap gap-1.5">
              {scale.map((label, idx) => {
                const v = idx + 1;
                const active = obj[stmt] === v;
                return (
                  <button key={label} type="button" disabled={disabled}
                    onClick={() => set({ ...obj, [stmt]: v })}
                    className={`rounded-full border px-2.5 py-1 text-[11px] transition ${
                      active ? "border-[#B45309] bg-[#B45309] text-white" : "border-neutral-200 text-neutral-700 hover:border-neutral-300"
                    }`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (q.type === "conditional_single_select") {
    const obj = (value && typeof value === "object") ? value : { choice: value || "", text: "" };
    return (
      <div className="space-y-2" data-testid={`qp-${q.id}`}>
        {(q.options || []).map((opt, i) => {
          const active = obj.choice === opt;
          return (
            <div key={`${i}-${opt}`}>
              <button type="button" disabled={disabled}
                onClick={() => set({ ...obj, choice: opt, text: opt === q.conditional_option ? obj.text : "" })}
                className={`flex min-h-14 w-full items-center justify-between rounded-xl border-2 p-4 text-left transition-all duration-150 active:scale-[0.98] ${
                  active ? "border-[#B45309] bg-amber-50" : "border-neutral-200 hover:border-neutral-300"
                }`}>
                <span className="text-base font-medium text-neutral-900">{opt}</span>
                <span className={`h-4 w-4 rounded-full border-2 ${active ? "border-[#B45309] bg-[#B45309]" : "border-neutral-300"}`} />
              </button>
              {opt === q.conditional_option && active && (
                <Input className="mt-2 h-11 rounded-lg border-2 focus:border-[#B45309]"
                  placeholder={q.conditional_placeholder || "Enter details..."}
                  value={obj.text || ""} disabled={disabled}
                  onChange={(e) => set({ ...obj, text: e.target.value })} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return <p className="text-xs text-neutral-500">Unknown question type: {q.type}</p>;
}

export { TERRA };
